import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { resolveStorefrontHost } from '@/features/storefront/storefrontHost';
import { storefrontLanguageHeaders } from '@/features/storefront/storefrontLanguageHeader';

/**
 * The shop's open positions, for the `/careers` pages and the `careers`
 * section.
 *
 * Mirrors `JobOpeningPublic` on the server. Like the team, the openings are
 * rows the merchant keeps on the Careers screen - never settings on a section -
 * so a filled role leaves every page at once. Only visible, unexpired openings
 * are ever sent, and the whole thing answers 404 `CAREERS_DISABLED` while the
 * merchant has the Careers page switched off.
 */

export const EMPLOYMENT_TYPES = [
  'FULL_TIME',
  'PART_TIME',
  'CONTRACT',
  'INTERNSHIP',
  'TEMPORARY',
  'VOLUNTEER',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const WORKPLACE_TYPES = ['ON_SITE', 'REMOTE', 'HYBRID'] as const;
export type WorkplaceType = (typeof WORKPLACE_TYPES)[number];

const text = z
  .string()
  .nullish()
  .transform((value) => value ?? null);

export const storefrontJobOpeningSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  department: text,
  location: text,
  // Caught rather than rejected: a type this build does not know yet costs
  // the opening its chip, not its place on the page.
  employment_type: z.enum(EMPLOYMENT_TYPES).catch('FULL_TIME'),
  workplace_type: z.enum(WORKPLACE_TYPES).catch('ON_SITE'),
  salary_text: text,
  summary: text,
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  apply_url: text,
  apply_email: text,
  /**
   * Whether the role's page carries the apply form. Read as off when a server
   * that predates the form leaves it out, so no shopper fills in a form
   * nothing would receive.
   */
  accepts_applications: z.boolean().catch(false).default(false),
  /** Whether that form insists on a CV. */
  resume_required: z.boolean().catch(false).default(false),
  /** `YYYY-MM-DD`, the last day applications are taken; null is open-ended. */
  closes_at: text,
  /** When it went up - an ISO timestamp, or a date. */
  posted_at: text,
});

export const storefrontCareersSchema = z.object({
  openings: z.array(storefrontJobOpeningSchema).default([]),
});

export type StorefrontJobOpening = z.infer<typeof storefrontJobOpeningSchema>;
export type StorefrontCareers = z.infer<typeof storefrontCareersSchema>;

export const NO_OPENINGS: StorefrontCareers = { openings: [] };

function publicHeaders(host?: string | null): Record<string, string> {
  const resolved = host ?? resolveStorefrontHost();
  return {
    ...(resolved ? { 'X-Storefront-Host': resolved } : {}),
    ...storefrontLanguageHeaders(),
  };
}

/** `GET /storefront/careers`. Public: no merchant token, the shop named in a header. */
export function fetchStorefrontCareers(host?: string | null): Promise<StorefrontCareers> {
  return apiRequest('/storefront/careers', storefrontCareersSchema, {
    cache: 'no-store',
    auth: 'none',
    headers: publicHeaders(host),
  });
}

/** `GET /storefront/careers/{slug}` - 404 for a hidden, closed or unknown opening. */
export function fetchStorefrontJobOpening(
  slug: string,
  host?: string | null,
): Promise<StorefrontJobOpening> {
  return apiRequest(`/storefront/careers/${encodeURIComponent(slug)}`, storefrontJobOpeningSchema, {
    cache: 'no-store',
    auth: 'none',
    headers: publicHeaders(host),
  });
}

/** What the apply form's resume may be: the server's own limits. */
export const RESUME_MAX_BYTES = 5 * 1024 * 1024;
export const RESUME_EXTENSIONS = ['pdf', 'doc', 'docx'] as const;
export const RESUME_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

/** The longest each field may be - the server's `JobApplicationCreate`. */
export const APPLICATION_LIMITS = {
  name: 120,
  email: 254,
  phone: 30,
  location: 140,
  url: 500,
  cover_letter: 5000,
  experience_max: 60,
} as const;

export const jobApplicationReceiptSchema = z.object({ id: z.string() });
export type JobApplicationReceipt = z.infer<typeof jobApplicationReceiptSchema>;

/**
 * `POST /storefront/careers/{slug}/applications`, as `multipart/form-data` -
 * the form's fields and the resume file in one request. Public: no token.
 */
export function submitJobApplication(
  slug: string,
  form: FormData,
  host?: string | null,
): Promise<JobApplicationReceipt> {
  return apiRequest(
    `/storefront/careers/${encodeURIComponent(slug)}/applications`,
    jobApplicationReceiptSchema,
    { method: 'POST', body: form, auth: 'none', headers: publicHeaders(host) },
  );
}
